import useSWRMutation from 'swr/mutation';

// Helpers
import helpers from 'frontend/js/helpers';
import shopifyHelpers from 'frontend/js/shopify';

// Store
import useLocalWishlistStore from '@src/stores/localWishlistStore';

// Fetch lists
// Get query
const getFetchCustomerListsQuery = () => {
  return (window as any).Alpine?.store('hasher')?.getQuery('get-wishlist-lists') || '';
};

// Fetcher
const fetchCustomerListsMutation = async (query: string, { arg }: { arg: { customerId: number } }) => {
  const { customerId } = arg;

  // Validate that we have the required query
  if (!query || query.trim() === '') {
    throw new Error("Can't trigger the mutation: missing key. GraphQL query not available.");
  }

  const hash = (window as any).Alpine?.store('hasher')?.getHash('get-wishlist-lists') || '';
  
  // Validate that we have the required hash
  if (!hash) {
    console.warn('Missing hash for get-wishlist-lists. Authentication may fail.');
  }

  const optionsData = {
    options: {
      method: 'POST',
      headers: {
        'X-Shopify-Access-Token': '<SHOPIFY_API_AUTHENTICATED_KEY>',
        'Content-Type': 'application/json',
        'X-MindArc-Hash': hash
      },
      body: JSON.stringify({ query, variables: { id: `gid://shopify/Customer/${customerId}` } }),
      request_body: query
    }
  };

  const jsonData = await shopifyHelpers.authenticatedFetch(optionsData);

  const { data, errors } = jsonData;

  if (errors) {
    const error = new Error('Failed to fetch customer wishlists');

    throw error;
  }

  return data.customer.wishlistLists?.references?.edges.map((edge) => ({
    id: edge.node.id,
    handle: edge.node.handle,
    title: helpers.getObjectFromKeyAndValue(edge.node.fields, 'key', 'list_name')?.value,
    listType: helpers.getObjectFromKeyAndValue(edge.node.fields, 'key', 'wishlist_id')?.value,
    products: JSON.parse(helpers.getObjectFromKeyAndValue(edge.node.fields, 'key', 'products')?.value)
  }));
};

// Update list
// Get query
// UpsertMetaobject mutation to update exiting metaobject or create if not exits
const getUpdateCustomerListQuery = () => {
  return (window as any).Alpine?.store('hasher')?.getQuery('metaobject-upsert') || '';
};

const getUpdateCustomerListInput = (handle: string, listName: string, products: string[]) => {
  return {
    handle: {
      type: 'wishlist',
      handle
    },
    metaobject: {
      // Pre-defined fields
      fields: [
        {
          key: 'list_name',
          value: listName
        },
        {
          key: 'wishlist_id',
          value: handle
        },
        {
          key: 'products',
          value: JSON.stringify(products)
        }
      ],
      capabilities: {
        publishable: {
          status: 'ACTIVE'
        }
      }
    }
  };
};

// Fetcher
const updateCustomerListMutation = async (
  query: string,
  { arg }: { arg: { handle: string; listName: string; products: string[] } }
) => {
  const { handle, listName, products } = arg;

  // Validate that we have the required query
  if (!query || query.trim() === '') {
    throw new Error("Can't trigger the mutation: missing key. GraphQL query not available.");
  }

  const metaobjectUpsertHash = (window as any).Alpine?.store('hasher')?.getHash('metaobject-upsert') || '';
  if (!metaobjectUpsertHash) {
    console.warn("Missing hash for metaobject-upsert. Authentication may fail.");
  }

  const optionsData = {
    options: {
      method: 'POST',
      headers: {
        'X-Shopify-Access-Token': '<SHOPIFY_API_AUTHENTICATED_KEY>',
        'Content-Type': 'application/json',
        'X-MindArc-Hash': metaobjectUpsertHash
      },
      body: JSON.stringify({ query, variables: getUpdateCustomerListInput(handle, listName, products) }),
      request_body: query
    }
  };

  const jsonData = await shopifyHelpers.authenticatedFetch(optionsData);

  const { data, errors } = jsonData;

  if (errors) {
    const error = new Error('Sorry, failed to create or update wishlist. Please try again another time');

    throw error;
  }

  return data.metaobjectUpsert.metaobject;
};

// Delete list
// Get query
const getDeleteCustomerListQuery = () => {
  return (window as any).Alpine?.store('hasher')?.getQuery('metaobject-delete') || '';
};

const getDeleteCustomerListInput = (id: string) => {
  return {
    id
  };
};

// Fetcher
const deleteCustomerListMutation = async (query: string, { arg }: { arg: { id: string } }) => {
  const { id } = arg;

  const optionsData = {
    options: {
      method: 'POST',
      headers: {
        'X-Shopify-Access-Token': '<SHOPIFY_API_AUTHENTICATED_KEY>',
        'Content-Type': 'application/json',
        'X-MindArc-Hash': (window as any).Alpine?.store('hasher')?.getHash('metaobject-delete') || ''
      },
      body: JSON.stringify({ query, variables: getDeleteCustomerListInput(id) }),
      request_body: query
    }
  };

  const jsonData = await shopifyHelpers.authenticatedFetch(optionsData);

  const { data, errors } = jsonData;

  if (errors) {
    const error = new Error('Sorry, failed to delete wishlist. Please try again another time');

    throw error;
  }

  return data.metaobjectDelete.deletedId;
};

// Add list to customer
const getCreateCustomerListQuery = () => {
  return (window as any).Alpine?.store('hasher')?.getQuery('update-customer-metafields') || '';
};

const getCreateCustomerListInput = (customerId: Number, wishlistLists: string[]) => {
  return {
    input: {
      metafields: [
        {
          namespace: 'custom',
          key: 'wishlist_lists',
          value: JSON.stringify(wishlistLists)
        }
      ],
      id: `gid://shopify/Customer/${customerId}`
    }
  };
};

// Fetcher
const createCustomerListMutation = async (query, { arg }: { arg: { customerId: number; wishlistLists: string[] } }) => {
  const { customerId, wishlistLists } = arg;

  // Validate that we have the required query
  if (!query || query.trim() === '') {
    throw new Error("Can't trigger the mutation: missing key. GraphQL query not available.");
  }

  const hash = (window as any).Alpine?.store('hasher')?.getHash('update-customer-metafields') || '';
  
  // Validate that we have the required hash
  if (!hash) {
    console.warn('Missing hash for update-customer-metafields. Authentication may fail.');
  }

  const optionsData = {
    options: {
      method: 'POST',
      headers: {
        'X-Shopify-Access-Token': '<SHOPIFY_API_AUTHENTICATED_KEY>',
        'Content-Type': 'application/json',
        'X-MindArc-Hash': hash
      },
      body: JSON.stringify({ query, variables: getCreateCustomerListInput(customerId, wishlistLists) }),
      request_body: query
    }
  };

  const jsonData = await shopifyHelpers.authenticatedFetch(optionsData);

  const { data, errors } = jsonData;

  if (errors) {
    const error = new Error('Failed to update wishlist lists. Please try again another time');

    throw error;
  }

  return data;
};

const useCustomerWishlist = () => {
  // Get local lists from store
  const localLists = useLocalWishlistStore((state) => state.localLists);

  // Get keys for mutations - ensure they exist before creating mutations
  const fetchCustomerListsKey = getFetchCustomerListsQuery();
  const updateCustomerListKey = getUpdateCustomerListQuery();
  const deleteCustomerListKey = getDeleteCustomerListQuery();
  const createCustomerListKey = getCreateCustomerListQuery();

  // Fetch customer lists
  const {
    data: customerLists,
    trigger: fetchCustomerLists,
    isMutating: isLoadingWishlist
  } = useSWRMutation(
    fetchCustomerListsKey || 'fetch-customer-lists-fallback', 
    fetchCustomerListsMutation
  );

  // Update customer list
  const {
    trigger: updateCustomerList,
    isMutating: updatingCustomerList,
    error: updateCustomerListError
  } = useSWRMutation(
    updateCustomerListKey || 'update-customer-list-fallback', 
    updateCustomerListMutation
  );

  // Delete customer list
  const {
    trigger: deleteCustomerList,
    isMutating: deletingCustomerList,
    error: deleteCustomerListError
  } = useSWRMutation(
    deleteCustomerListKey || 'delete-customer-list-fallback', 
    deleteCustomerListMutation
  );

  // Create customer list
  const { trigger: createCustomerList } = useSWRMutation(
    createCustomerListKey || 'create-customer-list-fallback', 
    createCustomerListMutation
  );

  // Helper function to check if a product is in wishlist
  const isProductInWishlist = (productId: string | number, listType: string = 'products'): boolean => {
    const currentList = localLists.find((list) => list.listType === listType);
    if (!productId || !currentList) return false;
    return currentList.products.some((productIdInList) => productIdInList.includes(productId.toString()));
  };

  return {
    customerLists,
    fetchCustomerLists,
    isLoadingWishlist,

    updateCustomerList,
    updatingCustomerList,
    updateCustomerListError,

    deleteCustomerList,
    deletingCustomerList,
    deleteCustomerListError,

    createCustomerList,

    // Helper functions
    isProductInWishlist
  };
};

export default useCustomerWishlist;
